/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import { types } from 'mobx-state-tree';

import { listPipelineRunOutput } from '../../helpers/api';
import { BaseStore } from '../BaseStore';

const File = types.model('File', {
  fileType: types.string,
  fileName: types.string,
  size: types.optional(types.integer, 0),
  lastModified: types.optional(types.Date, new Date()),
  fullPath: types.maybeNull(types.string),
  accessUrl: types.optional(types.string, ''),
});

const FilesStore = BaseStore.named('FilesStore')
  .props({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    progress: 0,
    runId: '',
    selectedPrefix: '',
    parentPrefix: '',
    files: types.array(File),
    visibleRecords: types.optional(types.array(File), []),
  })

  .actions(self => {
    // save the base implementation of clear
    const superCleanup = self.cleanup;

    return {
      async doLoad({ page = 1, pageSize = 10, reload = false, selectedPrefix } = {}) {
        self.currentPage = page;
        self.pageSize = pageSize;
        self.selectedPrefix = typeof selectedPrefix !== 'undefined' && selectedPrefix !== '' ? selectedPrefix : '';

        if (reload) {
          // Retrieve files
          self.progress = 0;
          self.total = 0;
          self.currentPage = 1;
          self.visibleRecords = [];

          let result = {};
            let fullDirList = {};

          // retrieve everything in current prefix
          fullDirList = (await listPipelineRunOutput(self.runId, self.selectedPrefix)).files;
          let mergedFiles = fullDirList;
          result = fullDirList;

          if (result === null || result.length === 0) {
            self.runInAction(() => {
              self.files = [];
            });
          } else {
            let files = mergedFiles
              .sort((fileA, fileB) => fileA.fileType.localeCompare(fileB.fileType))
              .map(file => {
                if (file.fileType === 'file') {
                  return { ...file, lastModified: new Date(file.lastModified) };
                } 
                  return { ...file };
                
              });

            // Update store
            self.runInAction(() => {
              self.parentPrefix = result.parentPrefix;
              self.files.replace(files);
              self.total = self.files.length;
              let num = self.currentPage * self.pageSize;
              self.visibleRecords.replace(_.cloneDeep(self.files.slice(num - self.pageSize, num)));
              self.progress = 100;
            });
          }
        } else {
          self.runInAction(() => {
            self.progress = 0;
            let num = self.currentPage * self.pageSize;
            self.progress = 100;
            self.visibleRecords.replace(_.cloneDeep(self.files.slice(num - self.pageSize, num)));
          });
        }
      },

      cleanup: () => {
        self.runInAction(() => {
          self.files.clear();
          superCleanup();
          self.clearSearch();
          self.currentPage = 1;
          self.total = 0;
          self.visibleRecords = [];
          self.selectedFiles = [];
          self.progress = 0;
          self.parentCollections = [];
        });
      },

      empty() {
        self.files.clear();
      },
    };
  })
  .views(self => ({
    get isEmpty() {
      return self.files.length === 0;
    },
    get totalPages() {
      return self.total === 0 ? 1 : Math.ceil(self.total / self.pageSize);
    },
  }));

// Note: Do NOT register this in the global context, if you want to gain access to an instance
//       use study.getFilesStore()
export { FilesStore }; // eslint-disable-line import/prefer-default-export
